import classNames from 'classnames';
import { geLegendBarStyling } from 'components/map/map-controller/tiles/image-map-scrubber/keys/utils.js';

type Subtract<A extends number, B extends number> =
  BuildTuple<A> extends [...infer U, ...BuildTuple<B>] ? Length<U> : never;

type Length<T extends unknown[]> = T extends { length: infer L } ? L : never;
type BuildTuple<L extends number, T extends unknown[] = []> = T extends { length: L } ? T : BuildTuple<L, [...T, unknown]>;
type LabelAlignment = 'left' | 'centre';

export interface FixedLengthArray<T, L extends number> extends Array<T> {
  0: T;
  length: L;
}

/**
 * We want the vals to be at low point, high point and intersection
 * So we need values which have an array of one more than the colors
 * Then we reduce the margin of the colors slightly so the first last value is over the point where the color bar starts
 * And the others have a fixed width
 */
export const LegendColorBarValuesOverDivision = <N extends number>({
  vals,
  colors,
  valsClasses,
}: {
  vals: FixedLengthArray<string, N>;
  colors: FixedLengthArray<string, Subtract<N, 1>>;
  valsClasses?: string;
}) => {
  const flexBaArr = vals.length / 100;
  const flexBasisArr = vals.map((_) => flexBaArr);

  return (
    <FlexColorKey
      vals={vals}
      colors={colors}
      flexBasisArr={flexBasisArr}
      marginValsIn={6}
      marginValsOut={-12}
      valsClasses={valsClasses}
    />
  );
};


/**
 * We want the vals to above each color section
 */
export const LegendColorBarValuesOverSection = <N extends number>({
  vals,
  colors,
  valsClasses,
  alignment
}: {
  vals: FixedLengthArray<string, N>;
  colors: FixedLengthArray<string, N>;
  valsClasses?: string;
  alignment?: LabelAlignment;
}) => {
  const flexBaArr = vals.length / 100;
  const flexBasisArr = vals.map((_) => flexBaArr);
  return (
    <FlexColorKey vals={vals} colors={colors} flexBasisArr={flexBasisArr} valsClasses={valsClasses} alignment={alignment} />
  );
};


/**
 * A colour row with labels above it which can be left-aligned or centred. 
 */
export const FlexColorKey = ({
  vals,
  valsClasses,
  colors,
  flexBasisArr,
  marginValsIn = 0,
  marginValsOut = 0,
  alignment = 'centre'
}: {
  vals: string[];
  valsClasses?: string;
  colors: string[];
  flexBasisArr: number[];
  marginValsIn?: number;
  marginValsOut?: number;
  alignment?: LabelAlignment;
}) => {
  const GetFlexElements = ({ index }: { index: number }) => {
    return (
      <div className="u-flex-1 u-flex u-flexCol" key={index} style={{ flexBasis: `${flexBasisArr[index]}` }}>
        <div
          className="u-flex-1"
          style={{
            ...{ lineHeight: '8px', backgroundColor: colors[index] ?? 'inherit' },
            ...geLegendBarStyling(index, colors.length),
          }}
        >
          &nbsp;
        </div>
      </div>
    );
  };

  const LabelRowLeftAligned = () => {
    return <div className="u-flex u-text-xs u-textNoWrap" style={{ margin: `0 ${marginValsOut}px`, height: '1.3rem', position: 'relative' }}>
      {vals.map((val, i) => {
        return (
          <span
            className={classNames('u-flex', valsClasses)}
            key={`${vals[i]}-${i}`}
            style={{
              position: i > 0 ? 'absolute' : 'relative',
              transform: i > 0 ? 'translateX(-50%)' : 'none',
              left: ((i / vals.length) * 100) + '%'
            }}
          >
            {val}
          </span>
        );
      })}
    </div>
  }

  const LabelRowCentered = () => {
    return <div className="u-flex u-flex-1 u-text-xs  u-textCenter u-textNoWrap" style={{ margin: `0 ${marginValsOut}px` }}>
      {vals.map((val, i) => {
        return (
          <span
            className={classNames('u-flex-1', valsClasses)}
            key={`${vals[i]}-${i}`}
            style={{ flexBasis: `${flexBasisArr[i].toString()}%` }}
          >
            {val}
          </span>
        );
      })}
    </div>;
  }

  return (
    <>
      <div className="u-flexCol u-flex u-text-xs u-textNoWrap {alignment === 'centre' ? u-'textCenter':''}" style={{ margin: `0 ${marginValsIn}px` }}>
        {alignment === 'left' ? <LabelRowLeftAligned /> : <LabelRowCentered />}
        <div className="u-textCenter u-flex u-text-xs u-textNoWrap">
          {colors.map((col, i) => {
            return <GetFlexElements key={col + i} index={i} />;
          })}
        </div>
      </div>
    </>
  );
};
